// This MUST be the first import to ensure error handlers are registered before
// webpack's dev server overlay handlers. Do not move this import.
// Trigger deployment for backend proxy testing
import "./suppressResizeObserverErrors";

import {createTheme, ThemeProvider} from "@material-ui/core/styles";
import {init, events, network, vitals, markers, profiler, paint} from "@palette.dev/browser";

// Only comment out the below line when you want to monitor re-rendering for perf purposes.
// import "./wdyr";

import ReactDOM from "react-dom/client";
import Moment from "react-moment";

import App from "./App";
import "./index.css";
import IntercomProvider from "./components/common/components/intercom/IntercomProvider";
import {FluxServicesContext} from "./injection/hooks";
import {getActiveServicesContainerSafelyFromRoot} from "./injection/singleton";
import {FluxLogger} from "./modules/storage_engine/connectors/LogConnector";
import {getThemeConfig} from "./resources/Theme";

// Eagerly initialize document effect services so they subscribe to the Zustand document store.
// These services (actionRecordService, documentSaveService, patchHandlerService, etc.) need
// to be created early to receive actions via Zustand's native subscribe().
import "./serviceInitializer";

const PALETTE_ENABLED_KEY = "flux_performance_metrics_enabled";

const getPaletteMode = () => {
    try {
        const storedValue = localStorage.getItem(PALETTE_ENABLED_KEY);
        return storedValue || "disabled";
    } catch (e) {
        return "disabled";
    }
};

const paletteMode = getPaletteMode();
const shouldInitializePalette = paletteMode === "report" || paletteMode === "report-profiling";

if (shouldInitializePalette && process.env.REACT_APP_PALETTE_CLIENT_KEY) {
    // eslint-disable-next-line no-console -- allow console for initialization info
    console.log("Initializing Palette with client key:", process.env.REACT_APP_PALETTE_CLIENT_KEY);

    init({
        key: process.env.REACT_APP_PALETTE_CLIENT_KEY,
        plugins: [events(), network(), vitals(), markers(), profiler(), paint()],
    });

    if (paletteMode === "report-profiling") {
        profiler.on(
            [
                "paint.click",
                "paint.keydown",
                "paint.scroll",
                "paint.mousemove",
                "markers.measure",
                "events.load",
                "events.dcl",
            ],
            {
                sampleInterval: 1,
                maxBufferSize: 100_000,
            },
        );
    }
} else if (!process.env.REACT_APP_PALETTE_CLIENT_KEY) {
    // eslint-disable-next-line no-console -- allow console for initialization info
    console.warn("Palette initialization skipped - no client key available");
}

// @ts-ignore
window.prerenderReady = false;

// The logger has to be initialized before the app fires up to catch all error
// events and perf traces.
FluxLogger.initialize();

// Start the pooled timer which runs every 60 seconds
Moment.startPooledTimer();

// Please keep your code out of here unless it truly need to run on ALL pages!

const theme = createTheme(getThemeConfig(true));

ReactDOM.createRoot(document.getElementById("root")!).render(
    <ThemeProvider theme={theme}>
        <FluxServicesContext.Provider value={getActiveServicesContainerSafelyFromRoot()}>
            <IntercomProvider>
                <App />
            </IntercomProvider>
        </FluxServicesContext.Provider>
    </ThemeProvider>,
);
